import { AnimatePresence, motion } from 'framer-motion';
import { Check as CheckIcon, ChevronRight, Download, FileSearch, Loader2, Radar, Send, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Check } from '@/lib/api/types';
import { downloadStatusReport } from '@/lib/download';
import { cn, maskPassport, timeAgo } from '@/lib/utils';
import { checkBadge, isInProgress, isVisaActive } from '@/lib/visa';
import { userMessage } from '@/lib/api/errors';

export function CheckBadge({ check }: { check: Check }) {
  const b = checkBadge(check);
  return (
    <Badge tone={b.tone} dot pulse={isInProgress(check.status)}>
      {b.label}
    </Badge>
  );
}

const STEPS = [
  { key: 'queued', label: 'Submitted', icon: Send },
  { key: 'running', label: 'Checking portal', icon: Radar },
  { key: 'completed', label: 'Result ready', icon: ShieldCheck },
] as const;

function stepIndex(check: Check): number {
  if (check.status === 'queued') return 0;
  if (check.status === 'running') return 1;
  return 2;
}

/** Horizontal stepper. The active step pulses while the check runs. */
export function CheckStepper({ check }: { check: Check }) {
  const idx = stepIndex(check);
  const failed = check.status === 'failed' || check.status === 'cancelled';
  return (
    <ol className="relative grid grid-cols-3 gap-2">
      <div className="absolute left-[16.6%] right-[16.6%] top-5 h-0.5 rounded-full bg-border" />
      <motion.div
        className={cn('absolute left-[16.6%] top-5 h-0.5 rounded-full', failed ? 'bg-rose-500' : 'bg-brand-gradient')}
        initial={false}
        animate={{ width: `${(idx / 2) * 66.8}%` }}
        transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      />
      {STEPS.map((s, i) => {
        const done = i < idx || (i === 2 && idx === 2);
        const active = i === idx && isInProgress(check.status);
        const Icon = s.icon;
        return (
          <li key={s.key} className="relative flex flex-col items-center gap-2 text-center">
            <motion.span
              initial={false}
              animate={{ scale: active ? [1, 1.08, 1] : 1 }}
              transition={active ? { repeat: Infinity, duration: 1.6 } : {}}
              className={cn(
                'relative z-10 flex size-10 items-center justify-center rounded-full border-2 bg-card transition-colors',
                done && !failed && 'border-transparent bg-brand-gradient text-primary-foreground',
                done && failed && i === 2 && 'border-rose-500 bg-rose-500 text-white',
                active && 'border-primary text-primary-ink',
                !done && !active && 'text-muted-foreground',
              )}
            >
              {active && <span className="absolute inset-0 animate-ping rounded-full bg-primary/20" />}
              {done && !(failed && i === 2) ? <CheckIcon className="size-4" strokeWidth={3} /> : <Icon className="size-4" />}
            </motion.span>
            <span className={cn('text-xs font-semibold', active || done ? 'text-foreground' : 'text-muted-foreground')}>
              {i === 2 && failed ? (check.status === 'cancelled' ? 'Cancelled' : 'Failed') : s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Animated "scanning" visual shown while a check is queued or running. */
export function ScanningVisual({ running }: { running: boolean }) {
  return (
    <div className="relative mx-auto flex size-44 items-center justify-center">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="absolute inset-0 rounded-full border border-primary/30"
          initial={{ scale: 0.4, opacity: 0.8 }}
          animate={{ scale: 1.15, opacity: 0 }}
          transition={{ repeat: Infinity, duration: 2.4, delay: i * 0.8, ease: 'easeOut' }}
        />
      ))}
      <div className="absolute inset-6 rounded-full bg-gradient-to-br from-brand-sun/15 to-brand-navy/15" />
      {running && (
        <motion.div
          className="absolute inset-6 rounded-full"
          style={{ background: 'conic-gradient(from 0deg, transparent 0deg, hsl(var(--brand-sun) / 0.45) 60deg, transparent 90deg)' }}
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 2.2, ease: 'linear' }}
        />
      )}
      <div className="relative flex size-20 items-center justify-center overflow-hidden rounded-2xl bg-card shadow-glow">
        <FileSearch className="size-9 text-primary-ink" />
        <span className="absolute inset-x-2 top-2 h-0.5 animate-scan-line rounded-full bg-brand-sun/70 shadow-[0_0_10px_hsl(var(--brand-sun))]" />
      </div>
    </div>
  );
}

/** Elapsed timer since a given ISO time, ticking each second. */
export function Elapsed({ since }: { since: string }) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  const m = Math.floor(secs / 60);
  const s = String(secs % 60).padStart(2, '0');
  return <span className="tabular-nums">{`${m}:${s}`}</span>;
}

export function DownloadReportButton({ check, className, size }: { check: Check; className?: string; size?: 'default' | 'lg' | 'sm' }) {
  const [busy, setBusy] = React.useState(false);
  // The backend sets report_url only for ACTIVE visas, and refuses the download otherwise.
  if (!check.report_url) return null;
  return (
    <Button
      variant="brand"
      size={size ?? 'lg'}
      className={className}
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadStatusReport(check);
          toast.success('Report downloaded');
        } catch (e) {
          toast.error('Could not download the report', { description: userMessage(e) });
        } finally {
          setBusy(false);
        }
      }}
    >
      {!busy && <Download />} Download status report
    </Button>
  );
}

/** Compact row for check lists. */
export function CheckRow({ check, to, showUser = false }: { check: Check; to: string; showUser?: boolean }) {
  const active = isVisaActive(check);
  return (
    <Link
      to={to}
      className="group flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-secondary/50 sm:px-5"
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-xl',
          active ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-secondary text-muted-foreground',
        )}
      >
        {isInProgress(check.status) ? <Loader2 className="size-4 animate-spin text-primary-ink" /> : active ? <ShieldCheck className="size-5" /> : <FileSearch className="size-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-sm font-semibold tracking-wide">{maskPassport(check.passport_no)}</p>
        <p className="truncate text-xs text-muted-foreground">
          {showUser && check.username ? `@${check.username} · ` : ''}
          {check.nationality_text} · {timeAgo(check.created_at)}
        </p>
      </div>
      <CheckBadge check={check} />
      <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

/** Field grid of the ICP result, with an "ACTIVE" stamp that lands on reveal. */
export function ResultCard({ check }: { check: Check }) {
  const r = check.result;
  if (!r) return null;
  const active = isVisaActive(check);
  const fields: [string, string][] = [
    ['File No.', r.file_no],
    ['Unified No.', r.unified_number],
    ['Issuance date', r.issuance_date],
    ['Last entry date', r.last_entry_date],
    ['Passport No.', check.passport_no],
    ['Passport expiry', check.passport_expiry],
  ];
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border p-5 sm:p-7',
        active
          ? 'border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.07] via-card to-teal-500/[0.07]'
          : 'border-amber-500/30 bg-gradient-to-br from-amber-500/[0.07] via-card to-card',
      )}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 size-60 rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">File status</p>
          <p className={cn('mt-1 text-3xl font-black tracking-tight', active ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')}>
            {r.file_status || '—'}
          </p>
        </div>
        <AnimatePresence>
          {active && (
            <motion.div
              initial={{ scale: 2.4, opacity: 0, rotate: -40 }}
              animate={{ scale: 1, opacity: 1, rotate: -10 }}
              transition={{ delay: 0.25, type: 'spring', stiffness: 260, damping: 13 }}
              className="rounded-xl border-[3px] border-emerald-500 px-4 py-1.5 font-black tracking-[0.2em] text-emerald-600 shadow-[0_0_40px_-6px_rgba(16,185,129,0.6)] dark:text-emerald-400"
            >
              ACTIVE
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <dl className="relative mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {fields.map(([k, v], i) => (
          <motion.div
            key={k}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className="border-b border-dashed pb-2"
          >
            <dt className="text-xs font-medium text-muted-foreground">{k}</dt>
            <dd className="mt-0.5 font-mono text-base font-semibold">{v || '—'}</dd>
          </motion.div>
        ))}
      </dl>
    </div>
  );
}
